<template>
  <div class="page-block">
    <div class="page-block__heading page-block__heading--withbar">
      <h3 class="page-block__title">SMS認証 - 認証コード入力</h3>
    </div>
    <div class="page-block__inner">
      <release-step-area
        v-if="!hideSteps"
        :index="4" />
      <div>
        <div class="verify-code-area">
          <div class="verify-code-area-title">認証コードの入力</div>
          <p class="verify-code-area-paragraph">
            携帯電話番号（末尾：{{ lastFourNumber() }}）にショートメッセージを送信しました。記載されている認証コードを入力して{{ nextPageText() }}
          </p>
          <div class="form-group b-0">
            <div class="form-item">
              <input
                v-model="code"
                class="form-control"
                type="text"
                placeholder="認証コード"
                maxlength="6"
                @input="onInput()" />
              <p
                v-if="errorMessage"
                class="error-text">
                {{ errorMessage }}
              </p>
            </div>
          </div>
          <div class="c-btn-wrap c-btn-wrap--two-btn">
            <a
              class="c-btn"
              :class="{ 'c-btn--off' : disableBtnClick }"
              :disabled="disableBtnClick"
              @click="submitSecurityCode">
              {{ nextPageText(true) }}
              <span class="c-btn__icon-arrow"></span>
            </a>
            <a
              class="c-btn c-btn--back c-btn--back-left"
              @click="onReturnClicked">
              戻る
              <span class="c-btn__icon-arrow c-btn__icon-arrow--left"></span>
            </a>
          </div>
          <p class="verify-code-area-paragraph-second">
            認証コードが届かなかった場合は<a
              class="c-text-link"
              @click="onReturnClicked">こちら</a>から電話番号を確認の上、認証コードを再送信してください。<br />
            <br />
            ご不明点はこちらの<a
              class="c-text-link"
              href="/client/contact"
              target="_blank">お問い合わせフォーム</a>からお問い合わせください。
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import ReleaseStepArea from '../Release/StepArea'

export default {
  name: 'SecurityCodeRegistration',
  components: {
    ReleaseStepArea,
  },
  props: {
    mobileNumber: {
      type: [String, Number],
      required: false,
      default: null,
    },
    isSubscriber: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasSettled: {
      type: Number,
      required: false,
      default: 0,
    },
    isMate: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPayment: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideSteps: {
      type: Boolean,
      default: false,
    },
    nextPageTitle: {
      type: String,
      required: false,
      default: '決済',
    },
  },
  data() {
    return {
      code: '',
      countryCode: '+81',
      errorMessage: '',
      isLoading: false,
      disableBtnClick: false,
      isFirstTime: true,
    }
  },
  methods: {
    // sanitize input to include only numbers
    sanitizeInput() {
      const validInputRegex = /^[0-9]+$/
      if (validInputRegex.test(this.code)) {
        this.code = this.code.replace(/[^0-9]+/g, '')
      } else {
        this.code = ''
      }
    },
    // handle user input
    onInput() {
      if (this.code) {
        this.clearErrorMessage()
      }
      this.sanitizeInput()
    },
    // handle submit of security code
    async submitSecurityCode() {
      if (!this.code) {
        this.errorMessage = '認証コードは必須です。'
        return
      }
      if (this.code.length !== 6) {
        this.errorMessage = '認証コードに誤りがあります。'
        return
      }
      try {
        this.isLoading = true
        this.disableBtnClick = true
        const response = await window.axios.post('/api/client/verify-code', {
          mobileNumber: this.formattedNumber(),
          code: this.code,
        })
        this.isLoading = false
        this.disableBtnClick = false
        if (!response.data.error && response.status === 200) {
          this.$emit('securityCodeResponse', {
            response,
          })
          // clearing remaining session key after successful verification
          if (window.sessionStorage.getItem('new_mobile_registration') === 'true') {
            window.sessionStorage.removeItem('new_mobile_registration')
          }
          window.sessionStorage.removeItem('account_information')
        } else if (response.data.error && response.status === 200) {
          this.errorMessage = response.data.message
          // if retry limit exceedeed, display mobile input screen with error message
          if (response.data.attempts > 3) {
            this.$emit('displayMobileRegistration')
            this.$emit('setErrorMessage', {
              error: this.errorMessage,
            })
            this.$emit('emitIsFirstTime', { firstTime: false })
          }
        }
      } catch ({ response }) {
        this.isLoading = false
        this.disableBtnClick = false
        const { data, status } = response
        if (data && status === 422) {
          const { errors } = data
          const [firstErrorMessage] = errors.code
          this.errorMessage = firstErrorMessage
        }
      }
    },
    // handle back operation
    onReturnClicked() {
      this.$emit('displayMobileRegistration', { display: true })
      this.$emit('emitIsFirstTime', { firstTime: false })
      if (window.sessionStorage.getItem('new_mobile_registration') === 'true') {
        window.sessionStorage.setItem('is_back_after_new_mobile_registration', 'true')
      }
    },
    // get last four digit of input mobile number
    lastFourNumber() {
      return this.mobileNumber.substring(this.mobileNumber.length - 4)
    },
    // clear error
    clearErrorMessage() {
      this.errorMessage = ''
    },
    // convert full width mobile number to half width
    convertToHalfWidth(mobileNo) {
      const fullWidthNumbers = '０１２３４５６７８９'
      const halfWidthNumbers = '0123456789'
      // Create a regular expression to match any full-width number
      const fullWidthRegex = new RegExp(`[${fullWidthNumbers}]`, 'g')
      // Replace full-width numbers with half-width numbers
      return mobileNo.replace(fullWidthRegex, (match) => {
        const index = fullWidthNumbers.indexOf(match)
        return index !== -1 ? halfWidthNumbers[index] : match
      })
    },
    // format mobile number in E.164 format
    formattedNumber() {
      return this.countryCode.concat(
        this.convertToHalfWidth(this.mobileNumber).slice(1, 11),
      )
    },
    nextPageText(button = false) {
      if (this.isPayment) {
        return `${this.nextPageTitle}${button ? 'を依頼する' : 'を依頼してください。'}`
      } if (this.isSubscriber || this.hasSettled === 1 || this.isMate) {
        return `配信審査${button ? 'を依頼する' : '依頼をしてください。'}`
      }
      return this.nextPageTitle + (button ? 'にすすむ' : 'に進んでください。')
    },
  },
}
</script>

<style scoped>
.error-text {
  color: red;
  display: block;
}
.c-text-link {
  color: #0b57d0;
  cursor: pointer;
  text-decoration: none;
}
.c-text-link:hover {
  color: blue;
  text-decoration: underline;
}
.b-0 {
  border: none !important;
  margin-bottom: 0 !important;
}
@media screen and (min-width: 896px) {
  .form-group {
    padding: 0;
  }
  .form-control {
    font-size: 16px;
  }
}
</style>
